import { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useParams } from "react-router-dom";
import {
  faChevronLeft,
  faBuilding,
  faPhone,
  faAddressBook,
  faLocation,
  faLocationArrow,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";

import Loading from "../../layout/Loading";
import ConfirmDelete from "../../modals/ConfirmDelete";
import UsersListPage from "../user/UserListPage.jsx";
import Button from "../../core/Button.jsx";

import useTriggeredAPICall from "../../../hooks/fetch-hooks/useTriggeredAPICall";
import useAPICall from "../../../hooks/fetch-hooks/useAPICall";
import useDelayedLoading from "../../../hooks/useDelayedLoading";
import { useAuthInfo } from "../../../context/AuthContext";

import { formatPhone } from "../../../util/stringUtils";
import FormFieldTags from "../../input/FormFieldTags";
import { setCachedBreadcrumbLabel } from "../../../helpers/breadcrumbCache";

export default function OrganizationPage(props) {
  const orgIdParams = props.match.params.orgId;

  const { userInfo } = useAuthInfo();
  const { data: organization, isLoading, fetchData } = useTriggeredAPICall();
  const { fetchData: updateOrgActivation } = useTriggeredAPICall();
  const { data: users } = useAPICall(`/users/organization/${orgIdParams}`);

  const showLoading = useDelayedLoading(isLoading);

  const orgCheck = orgIdParams === userInfo?.org_id;

  async function handleActivation() {
    const newActiveStatus = !organization?.results.is_active;

    await updateOrgActivation(
      `/organization/${organization?.results.org_id}`,
      "PATCH",
      { is_active: newActiveStatus }
    );

    fetchData(`/organization/${orgIdParams}`);
  }

  const orgUsers = users?.results?.filter(
    (user) =>
      user?.organization?.org_id === orgIdParams &&
      user.is_login_enabled === true
  );

  useEffect(() => {
    if (!organization) {
      fetchData(`/organization/${orgIdParams}`);
    }
  }, [organization, fetchData, orgIdParams]);

  useEffect(() => {
    if (organization?.results?.org_id && organization?.results?.name) {
      setCachedBreadcrumbLabel(
        "organization",
        organization.results.org_id,
        organization.results.name
      );
    }
  }, [organization]);

  if (showLoading && !organization) {
    return <Loading />;
  }

  if (!organization) {
    return null;
  }

  return (
    <div className="width-full min-width-9">
      <div className="top-section flex-space-between">
        <button onClick={() => props.history.goBack()}>
          <FontAwesomeIcon icon={faChevronLeft} />
          Back
        </button>
        {userInfo.role.name !== "user" && (
          <div className="flex-start active-switch-div">
            Active:
            <label className="switch">
              <input
                type="checkbox"
                disabled={orgCheck}
                onChange={handleActivation}
                checked={organization.results.is_active}
              />

              <span
                className={`slider round ${orgCheck ? "disable-switch" : ""}`}
              >
                <span>On</span>
                <span>Off</span>
              </span>
            </label>
          </div>
        )}
      </div>

      <div className="width-full ">
        <div className=" card flex-rows flex-align-center flex-rows-space-around">
          <h1 className="pr-2">
            <FontAwesomeIcon icon={faBuilding} className="pr-2" />
            {organization.results.name}
          </h1>

          <div className="middle-section pt-4">
            <div className="icon-and-details">
              <div className="width-full">
                <div>
                  <FontAwesomeIcon icon={faLocationDot} />
                  <span className="pr-3">Address: </span>
                  {organization.results.address} - {organization.results.city}{" "}
                  {organization.results.state}
                  &nbsp;
                  {organization.results.zip_code}
                </div>
              </div>
              <p className="phone">
                <FontAwesomeIcon icon={faPhone} />
                <span className="pr-3">Phone: </span>
                {formatPhone(organization.results.phone)}
              </p>
              <div className="width-full flex-rows ">
                <span className="pr-3">Tags:</span>
                <FormFieldTags selectedTags={organization.results.tags} />
              </div>
            </div>
            <div className="flex-rows flex-align-start button-row-right">
              {userInfo.role.name !== "user" && (
                <Button
                  to={`/organization-form/${organization.results.org_id}`}
                >
                  Edit
                </Button>
              )}

              {userInfo.organization.org_id !== orgIdParams && (
                <ConfirmDelete
                  objectType="organization"
                  id={organization.results.org_id}
                  users={users?.results}
                />
              )}
            </div>
          </div>
        </div>

        <br />

        <UsersListPage
          {...props}
          columns="first_name,email,phone,is_active"
          orgId={orgIdParams}
          userList={orgUsers}
          orgName={organization.results.name}
          disableAddUser={!organization.results.is_active}
        />
      </div>
    </div>
  );
}
